import type { BotDefeatRuntime } from "../application/bot-defeat-runtime";
import type { GameFlow } from "../application/game-flow";
import type { GameplayLifecycle } from "../application/gameplay-lifecycle";
import type { PlayerDefeatRuntime } from "../application/player-defeat-runtime";
import type { PlayerRuntime } from "../application/player-runtime";
import type { ProgressState } from "../application/progress-state";
import type { ResultsRuntime } from "../application/results-runtime";
import type { TailClipRuntime } from "../application/tail-clip-runtime";
import type {
  AudioAutoplayState,
  AudioBoundary,
  AudioCueIdentifier,
  SoundtrackCacheSnapshot,
} from "./audio-contract";
import { gameplayConfig } from "../config/gameplay-config";
import { recordBoundedIdentifier } from "../application/runtime-stability";
import {
  defaultSelectedSoundtrackId,
  type SelectedSoundtrackId,
} from "../config/soundtrack-config";
import type { BonusRuntime } from "../application/bonus-runtime";

export interface AudioGestureSource {
  addEventListener(type: "pointerdown", listener: () => void): void;
  removeEventListener(type: "pointerdown", listener: () => void): void;
}

export interface AudioPreferenceSource {
  getState(): ProgressState;
}

export interface AudioRuntimeOptions {
  readonly bonusRuntime?: Pick<BonusRuntime, "getSnapshot" | "subscribe">;
  readonly botDefeatRuntime: Pick<BotDefeatRuntime, "subscribe">;
  readonly boundary: AudioBoundary;
  readonly flow: Pick<GameFlow, "getSnapshot" | "subscribe">;
  readonly gestureSource: AudioGestureSource;
  readonly lifecycle: Pick<GameplayLifecycle, "subscribe">;
  readonly playerDefeatRuntime: Pick<
    PlayerDefeatRuntime,
    "getSnapshot" | "subscribe"
  >;
  readonly playerRuntime: Pick<PlayerRuntime, "getSnapshot" | "subscribe">;
  readonly preferences: AudioPreferenceSource;
  readonly resultsRuntime: Pick<ResultsRuntime, "subscribe">;
  readonly tailClipRuntime?: Pick<TailClipRuntime, "subscribe">;
}

const recentCueLimit = 12;

export interface AudioRuntimeState {
  readonly activeTrack: SelectedSoundtrackId | null;
  readonly cueLog: readonly AudioCueIdentifier[];
  readonly musicDisabled: boolean;
  readonly permission: AudioAutoplayState;
  readonly scoreUrl: string;
  readonly soundDisabled: boolean;
  readonly soundtrackCache: SoundtrackCacheSnapshot;
}

export interface AudioRuntimeStabilitySnapshot {
  readonly processedEventCount: number;
  readonly subscriberCount: number;
}

export class AudioRuntime {
  private activeTrack: AudioRuntimeState["activeTrack"] = null;
  private permission: AudioRuntimeState["permission"] = "pending";
  private destroyed = false;
  private gestureReceived = false;
  private musicMuted = false;
  private selectedTrack: SelectedSoundtrackId = defaultSelectedSoundtrackId;
  private operation = Promise.resolve();
  private platformPaused = false;
  private readonly processedEventKeys = new Set<string>();
  private cueLog: AudioCueIdentifier[] = [];
  private scene: "gameplay" | "home" | "results" = "home";
  private paused = false;
  private soundMuted = false;
  private readonly unsubscribers: (() => void)[] = [];

  public constructor(private readonly options: AudioRuntimeOptions) {
    this.refreshPreferences();
    options.gestureSource.addEventListener("pointerdown", this.handleGesture);
    this.unsubscribers.push(
      options.flow.subscribe((snapshot) => {
        this.scene = snapshot.scene;
        this.refreshPreferences();
        this.syncMusic();
      }),
      options.lifecycle.subscribe((snapshot) => {
        if (snapshot.platformPaused && !this.platformPaused) {
          this.gestureReceived = false;
          this.permission = "blocked";
          void this.options.boundary.suspend();
        }
        this.platformPaused = snapshot.platformPaused;
        this.paused = snapshot.paused || snapshot.platformPaused;
        this.refreshPreferences();
        this.syncMusic();
      }),
      options.playerRuntime.subscribe((snapshot) => {
        const feedback = snapshot.feedback;
        if (feedback === null) {
          return;
        }
        const eventKey = `${String(snapshot.runId)}:pickup:${String(feedback.revision)}`;
        this.routeCue("pickup", eventKey);
        if (feedback.levelUp) {
          this.routeCue("level", `${eventKey}:level`);
        }
      }),
      options.botDefeatRuntime.subscribe((outcome) => {
        if (outcome.winnerId !== "player" || outcome.rewardCoins <= 0) {
          return;
        }
        this.routeCue(
          "win",
          `${String(outcome.runId)}:bot-defeat:${outcome.contactId}`,
        );
      }),
      options.playerDefeatRuntime.subscribe(() => {
        this.syncPlayerDefeat();
      }),
      options.resultsRuntime.subscribe((snapshot) => {
        const result = snapshot.result;
        if (result === null) {
          return;
        }
        this.routeCue("results", `${result.id}:results`);
      }),
    );
    const bonusRuntime = options.bonusRuntime;
    if (bonusRuntime !== undefined) {
      this.unsubscribers.push(
        bonusRuntime.subscribe(() => {
          const event = bonusRuntime.getSnapshot().lastEvent;
          if (event !== null) {
            this.routeCue(event.type, `bonus:${event.bonusId}`);
          }
        }),
      );
    }
    const tailClipRuntime = options.tailClipRuntime;
    if (tailClipRuntime !== undefined) {
      this.unsubscribers.push(
        tailClipRuntime.subscribe((snapshot) => {
          const clip = snapshot.lastClip;
          if (clip?.attackerId !== "player" || clip.victimDefeated) {
            return;
          }
          const runId = options.playerRuntime.getSnapshot().runId;
          this.routeCue(
            "crunch",
            `${String(runId)}:tail-clip:${String(clip.revision)}`,
          );
        }),
      );
    }
  }

  public destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    this.options.gestureSource.removeEventListener(
      "pointerdown",
      this.handleGesture,
    );
    for (const unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
    this.unsubscribers.length = 0;
    this.options.boundary.destroy();
    this.activeTrack = null;
  }

  public getState(): AudioRuntimeState {
    return {
      activeTrack: this.activeTrack,
      cueLog: [...this.cueLog],
      musicDisabled: this.musicMuted,
      permission: this.permission,
      scoreUrl: this.options.boundary.getAssetUrl(this.selectedTrack),
      soundDisabled: this.soundMuted,
      soundtrackCache: this.options.boundary.getSoundtrackCacheSnapshot(),
    };
  }

  public getStabilitySnapshot(): AudioRuntimeStabilitySnapshot {
    return {
      processedEventCount: this.processedEventKeys.size,
      subscriberCount: this.unsubscribers.length,
    };
  }

  public syncPreferences(): void {
    this.refreshPreferences();
    this.syncMusic();
  }

  private readonly handleGesture = (): void => {
    const permission = this.options.boundary.unlock();
    this.enqueue(async () => {
      const permitted = await permission;
      this.gestureReceived = permitted && !this.platformPaused;
      this.permission = this.gestureReceived ? "permitted" : "blocked";
      await this.applyMusicState();
    });
  };

  private enqueue(operation: () => Promise<void>): void {
    this.operation = this.operation.then(operation, operation);
  }

  private refreshPreferences(): void {
    const progress = this.options.preferences.getState();
    const preferences = progress.preferences;
    this.selectedTrack = progress.selectedSoundtrackId;
    this.musicMuted = !preferences.musicEnabled;
    this.soundMuted = !preferences.soundEnabled;
  }

  private routeCue(identifier: AudioCueIdentifier, eventKey: string): void {
    if (this.processedEventKeys.has(eventKey)) {
      return;
    }
    recordBoundedIdentifier(
      this.processedEventKeys,
      eventKey,
      gameplayConfig.runtimeStability.maximumProcessedEvents,
    );
    this.cueLog = [...this.cueLog, identifier].slice(-recentCueLimit);
    this.refreshPreferences();
    if (this.soundMuted || this.paused) {
      return;
    }
    this.enqueue(async () => {
      if (!this.gestureReceived) {
        this.permission = "blocked";
        return;
      }
      const result = await this.options.boundary.playCue(identifier);
      if (result === "blocked") {
        this.permission = "blocked";
      } else if (result === "played") {
        this.permission = "permitted";
      }
    });
  }

  private syncMusic(): void {
    this.refreshPreferences();
    if (this.shouldStopMusic()) {
      this.options.boundary.stopMusic();
      this.activeTrack = null;
    }
    this.enqueue(async () => {
      await this.applyMusicState();
    });
  }

  private shouldStopMusic(): boolean {
    return (
      this.scene !== "gameplay" ||
      this.paused ||
      this.musicMuted ||
      !this.gestureReceived
    );
  }

  private async applyMusicState(): Promise<void> {
    this.refreshPreferences();
    if (this.scene !== "gameplay" || this.paused || this.musicMuted) {
      this.options.boundary.stopMusic();
      this.activeTrack = null;
      return;
    }
    if (!this.gestureReceived) {
      this.options.boundary.stopMusic();
      this.activeTrack = null;
      this.permission = "blocked";
      return;
    }
    const requestedTrack = this.selectedTrack;
    const result = await this.options.boundary.startMusic(requestedTrack);
    this.refreshPreferences();
    if (this.shouldStopMusic() || this.selectedTrack !== requestedTrack) {
      this.options.boundary.stopMusic();
      this.activeTrack = null;
      return;
    }
    if (result === "played") {
      this.permission = "permitted";
      this.activeTrack = requestedTrack;
    } else {
      this.activeTrack = null;
      if (result === "blocked") {
        this.permission = "blocked";
      }
    }
  }

  private syncPlayerDefeat(): void {
    const snapshot = this.options.playerDefeatRuntime.getSnapshot();
    if (snapshot.state === "waiting" && snapshot.respawnAtMs !== null) {
      const runId = this.options.playerRuntime.getSnapshot().runId;
      this.routeCue(
        "defeat",
        `${String(runId)}:player-defeat:${String(snapshot.respawnAtMs)}`,
      );
    }
    const respawn = snapshot.lastRespawn;
    if (respawn !== null) {
      this.routeCue(
        "respawn",
        `${String(respawn.runId)}:respawn:${respawn.contactId}`,
      );
    }
  }
}
